import { ADDED_LANGS, type AddedLang } from "./locales";

/** Explicit, local translation catalogs. There is no network translation or dynamic code. */
export type Catalog = Readonly<Record<string, string>>;

/** Columns follow ADDED_LANGS. Escaped newlines are decoded after splitting rows and columns. */
export function readCatalogs(data: string): Record<AddedLang, Catalog> {
  const catalogs = Object.fromEntries(ADDED_LANGS.map((lang) => [lang, {}])) as Record<AddedLang, Record<string, string>>;
  for (const row of data.trim().split(/\r?\n/)) {
    if (!row.trim() || row.startsWith("#")) continue;
    const [key, ...values] = row.split("|");
    if (values.length !== ADDED_LANGS.length || !key) throw new Error(`Translation columns: ${key}`);
    ADDED_LANGS.forEach((lang, i) => {
      if (Object.hasOwn(catalogs[lang], key)) throw new Error(`Duplicate translation: ${key}`);
      if (!values[i].trim()) throw new Error(`Empty translation: ${lang}.${key}`);
      catalogs[lang][key] = values[i].replaceAll("\\n", "\n");
    });
  }
  return catalogs;
}

/** Numbered placeholders preserve argument order without depending on English word order. */
export function interpolate(template: string, args: readonly unknown[], locale: string): string {
  return template.replace(/\{(\d+)\}/g, (token, index: string) => {
    const value = args[Number(index)];
    if (value === undefined) throw new Error(`Missing translation argument ${token}`);
    return typeof value === "number" ? new Intl.NumberFormat(locale).format(value) : String(value ?? "");
  });
}

/** Fall back per entry while a catalog is being translated; never mutate the English source. */
export function translateCatalog<T>(source: T, catalog: Catalog, locale: string): T {
  const used = new Set<string>();
  const repeated = new Map<string, string>();
  // Reuse identical static text (e.g. "Subject" or "Email") across screens.
  const collect = (value: unknown, path: string): void => {
    if (typeof value === "string" && Object.hasOwn(catalog, path)) repeated.set(value, catalog[path]);
    else if (Array.isArray(value)) value.forEach((item, i) => collect(item, `${path}.${i}`));
    else if (value && typeof value === "object" && !Object.hasOwn(value, "$$typeof")) {
      for (const [key, item] of Object.entries(value)) collect(item, path ? `${path}.${key}` : key);
    }
  };
  collect(source, "");
  const visit = (value: unknown, path: string): unknown => {
    if (Object.hasOwn(catalog, path)) {
      if (typeof value !== "string" && typeof value !== "function") throw new Error(`Not a text entry: ${path}`);
      used.add(path);
      return typeof value === "function" ? (...args: unknown[]) => interpolate(catalog[path], args, locale) : catalog[path];
    }
    if (typeof value === "string" && repeated.has(value)) return repeated.get(value);
    if (Array.isArray(value)) return value.map((item, i) => visit(item, `${path}.${i}`));
    // JSX entries have React symbols and must remain opaque until translated explicitly.
    if (value && typeof value === "object" && !Object.hasOwn(value, "$$typeof")) {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, visit(item, path ? `${path}.${key}` : key)]));
    }
    return value;
  };
  const result = visit(source, "") as T;
  for (const key of Object.keys(catalog)) if (!used.has(key)) throw new Error(`Unknown translation key: ${key}`);
  return result;
}
